import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Outlet } from 'react-router-dom'
import _ from 'lodash'

import { useAuthStore } from '../auth.context/auth.context'
import { loadFeatureFlags } from '@services/hubii/hub-service'

import { socketClient } from '@services/hubii/notification-service'

import NotificationRing from '@assets/audio/notification-ring.mp3'
import NotificationError from '@assets/audio/notification-error.mp3'

import NotificationDialog from './components/notification-dialog'
import ErrorConnectionDialog from './components/error-connection-dialog'
import NotificationHandler from './notification-handler'

const NotificationContext = createContext<NotificationContextSpace.State>(
  {} as NotificationContextSpace.State,
)

export const useNotifications = () => useContext(NotificationContext)

const NotificationProvider = () => {
  const { user, selectedHub } = useAuthStore()

  const audio = useRef(new Audio(NotificationRing))
  const audioError = useRef(new Audio(NotificationError))
  const [persistentHubs, setPersistentHubs] = useState(false)

  useEffect(() => {
    const initAudio = async () => {
      try {
        await audio.current.load()
        await audioError.current.load()
      } catch (error) {
        console.error('Erro ao inicializar áudio:', error)
      }
    }

    initAudio()
  }, [])

  const [isOnline, setIsOnline] = useState(true)
  const [isAllowToAudio, setIsAllowToAudio] = useState(false)

  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isPageVisible, setIsPageVisible] = useState(document.visibilityState === 'visible')

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    const audioAllowed = localStorage.getItem('allow_to_audio') === 'true'
    setIsAllowToAudio(audioAllowed)
    requestNotificationPermission()

    if (audioAllowed) {
      socketClient.connect()
    }
  }, [])

  useEffect(() => {
    const loadHubFeatureFlags = async () => {
      if (!selectedHub?.uuid) return

      try {
        const flags = await loadFeatureFlags(selectedHub.uuid)
        if (flags.some((flag: { name: string }) => flag.name === 'persisted_notification')) {
          setPersistentHubs(true)
        } else {
          setPersistentHubs(false)
        }
      } catch (error) {
        console.error(`Erro ao carregar feature flags do hub ${selectedHub.uuid}:`, error)
      }
    }

    loadHubFeatureFlags()
  }, [selectedHub?.uuid])

  useEffect(() => {
    if (user?.hubs && user.hubs.length > 0 && socketClient.connected) {
      console.log('Socket inicial conectando em todos os hubs')
      user.hubs.forEach((hub) => {
        console.log('Socket Create: ', `hub-${hub.uuid}`)
        socketClient.emit('create', `hub-${hub.uuid}`)
      })
    }
  }, [user?.hubs, socketClient.connected])

  const setupSocketConnection = useCallback(() => {
    const handleConnect = () => {
      console.log('Socket Connected')

      if (user?.hubs && user.hubs.length > 0) {
        user.hubs.forEach((hub) => {
          console.log('Socket Create: ', `hub-${hub.uuid}`)
          socketClient.emit('create', `hub-${hub.uuid}`)
        })
      }

      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current)
        disconnectTimeoutRef.current = null
      }

      setIsOnline(true)
    }

    const handleDisconnect = (reason: any) => {
      console.log('disconnect', reason)
      scheduleDisconnection()
    }

    socketClient.on('connect', handleConnect)
    socketClient.on('disconnect', handleDisconnect)
    socketClient.on('connect_error', scheduleDisconnection)
    socketClient.on('connect_failed', scheduleDisconnection)
    socketClient.on('error', scheduleDisconnection)
    socketClient.on('reconnect_failed', scheduleDisconnection)

    if (!socketClient.connected) {
      socketClient.connect()
    }

    return () => {
      socketClient.off('connect', handleConnect)
      socketClient.off('disconnect', handleDisconnect)
      socketClient.off('connect_error', scheduleDisconnection)
      socketClient.off('connect_failed', scheduleDisconnection)
      socketClient.off('error', scheduleDisconnection)
      socketClient.off('reconnect_failed', scheduleDisconnection)
    }
  }, [user?.hubs])

  useEffect(() => {
    if (isAllowToAudio) {
      const cleanup = setupSocketConnection()

      return () => {
        cleanup()
        socketClient.disconnect()
      }
    }
  }, [isAllowToAudio, setupSocketConnection])

  const scheduleDisconnection = () => {
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current)
    }

    disconnectTimeoutRef.current = setTimeout(() => {
      audioError.current.play()
      setIsOnline(false)
    }, 60 * 1000)
  }

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('Este navegador não suporta notificações desktop')
      return
    }

    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()

      if (permission === 'granted') {
        localStorage.setItem('notification_permission', 'granted')
      }
    }
  }

  const isNotificationSupported = useCallback(() => {
    return 'Notification' in window && navigator.serviceWorker !== undefined
  }, [])

  const playNotificationSound = useCallback(() => {
    if (!audio.current) {
      console.warn('Elemento de áudio não inicializado')
      return Promise.resolve()
    }

    try {
      audio.current.pause()
      audio.current.currentTime = 0
      audio.current.loop = persistentHubs

      return new Promise<void>((resolve) => {
        const playPromise = audio.current.play()

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Áudio reproduzido com sucesso')
              resolve()
            })
            .catch((error) => {
              console.error('Erro ao reproduzir áudio:', error)
              resolve()
            })
        } else {
          resolve()
        }
      })
    } catch (error) {
      console.error('Erro ao manipular áudio:', error)
      return Promise.resolve()
    }
  }, [persistentHubs])

  const showNotification = useCallback((title: string, options: NotificationOptions) => {
    if (Notification.permission === 'granted') {
      return new Notification(title, options)
    }
    return null
  }, [])

  const handleAllowToAudio = () => {
    setIsAllowToAudio(true)
    localStorage.setItem('allow_to_audio', 'true')
    socketClient.connect()
  }

  const addPushNotification = async (title: string, notificationData: NotificationOptions) => {
    try {
      if (!('Notification' in window)) {
        console.error('Este navegador não suporta notificações')
        return
      }

      const permission = await Notification.requestPermission()
      console.log('Status da permissão:', permission)

      if (permission !== 'granted') {
        console.error('Permissão para notificações não foi concedida')
        return
      }

      try {
        const registration = await navigator.serviceWorker.ready
        console.log('Service Worker pronto para enviar notificações')
        console.log('Registration:', registration)

        console.log('Enviando notificação via Service Worker com dados:')
        await registration.showNotification(title, notificationData)
        console.log('Notificação enviada com sucesso via Service Worker')
      } catch (error) {
        console.error('Erro ao enviar notificação:', error)
      }
    } catch (error) {
      console.error('Erro geral na função de notificação:', error)
    }
  }

  const contextValue = useMemo(() => {
    return {
      isOnline,
      isAllowToAudio,
      isPageVisible,
      audio: audio.current,
      playNotificationSound,
      showNotification,
      isNotificationSupported,
      addPushNotification,
    }
  }, [
    isOnline,
    isAllowToAudio,
    isPageVisible,
    playNotificationSound,
    showNotification,
    isNotificationSupported,
    addPushNotification,
  ])

  return (
    <>
      <NotificationDialog open={!isAllowToAudio} onDismiss={handleAllowToAudio} />
      <ErrorConnectionDialog
        open={!isOnline && isAllowToAudio}
        onDismiss={() => setIsOnline(true)}
      />

      <NotificationContext.Provider value={contextValue}>
        <NotificationHandler />
        <Outlet />
      </NotificationContext.Provider>
    </>
  )
}

export default NotificationProvider

export namespace NotificationContextSpace {
  export type State = {
    isOnline: boolean
    isAllowToAudio: boolean
    isPageVisible: boolean
    audio: HTMLAudioElement | null
    playNotificationSound: () => Promise<void>
    showNotification: (title: string, options: NotificationOptions) => Notification | null
    isNotificationSupported: () => boolean
    addPushNotification: (title: string, options: NotificationOptions) => Promise<void>
  }
}
